/**
 * Static default values for SBI Core dashboard when USE_DEFAULTS is true.
 * Every selector option is listed: "all" (All data) and "week0".."week11" (last 12 weeks in dropdown order).
 * "All data" is the mean of week0..week11. Each metric has me and benchmark (overall + regional).
 */

function round2(x) {
  return Math.round(x * 100) / 100;
}

function mean(values) {
  if (!values.length) return 0;
  return values.reduce((s, v) => s + v, 0) / values.length;
}

function buildPatientSatisfaction(me, benchO, benchR) {
  return [
    { label: "Pre-med talk", meValue: me[0], benchmarkOverall: benchO[0], benchmarkRegional: benchR[0] },
    { label: "Induction Anaesthesia", meValue: me[1], benchmarkOverall: benchO[1], benchmarkRegional: benchR[1] },
    { label: "PACU Stays", meValue: me[2], benchmarkOverall: benchO[2], benchmarkRegional: benchR[2] },
  ];
}

function buildDataQuality(me, benchO, benchR) {
  return [
    { label: "Internal Consistency", meValue: me[0], benchmarkOverall: benchO[0], benchmarkRegional: benchR[0] },
    { label: "Quality of Evaluation", meValue: me[1], benchmarkOverall: benchO[1], benchmarkRegional: benchR[1] },
    { label: "Completeness of Data", meValue: me[2], benchmarkOverall: benchO[2], benchmarkRegional: benchR[2] },
  ];
}

function buildQuattroMetrics(me, benchO, benchR) {
  return [
    { label: "No Delirium / PND", color: "#e5e5e5", meValue: me[0], benchmarkOverall: benchO[0], benchmarkRegional: benchR[0] },
    { label: "No Nausea / Vomiting", color: "#80cbc4", meValue: me[1], benchmarkOverall: benchO[1], benchmarkRegional: benchR[1] },
    { label: "No High Pain", color: "#546e7a", meValue: me[2], benchmarkOverall: benchO[2], benchmarkRegional: benchR[2] },
    { label: "No High Discomfort", color: "#424242", meValue: me[3], benchmarkOverall: benchO[3], benchmarkRegional: benchR[3] },
    { label: "QUATTRO", color: null },
  ];
}

function buildPatientComfort(me, count, benchO, benchR) {
  const titles = [
    "Delirium / PND", "High Anxiety", "Long Fluid Fasting Time", "High Thirst", "High Pain",
    "Burst Suppression Time", "Throat/Airway discomfort", "Shivering", "High Stress",
    "Nausea / Vomiting", "Reduced well-being", "Hypothermia avoided",
  ];
  return titles.map((title, i) => ({
    title,
    meValue: me[i],
    count: count[i],
    benchmarkOverall: benchO[i],
    benchmarkRegional: benchR[i],
  }));
}

/** Derive Quattro me/benchO/benchR from patient comfort arrays: No Delirium=100-me[0], No Nausea=100-me[9], No High Pain=100-mean(Shivering,High Pain,Throat), No High Discomfort=100-mean(HA,HS,Rwb). */
function quattroFromPatientComfort(me, benchO, benchR) {
  return {
    me: [
      round2(100 - me[0]),
      round2(100 - me[9]),
      round2(100 - (me[7] + me[4] + me[6]) / 3),
      round2(100 - (me[1] + me[8] + me[10]) / 3),
    ],
    benchO: [
      round2(100 - benchO[0]),
      round2(100 - benchO[9]),
      round2(100 - (benchO[7] + benchO[4] + benchO[6]) / 3),
      round2(100 - (benchO[1] + benchO[8] + benchO[10]) / 3),
    ],
    benchR: [
      round2(100 - benchR[0]),
      round2(100 - benchR[9]),
      round2(100 - (benchR[7] + benchR[4] + benchR[6]) / 3),
      round2(100 - (benchR[1] + benchR[8] + benchR[10]) / 3),
    ],
  };
}

function buildPrevention(me, benchO, benchR) {
  return [
    { title: "PONV SOP applied", meValue: me[0], benchmarkOverall: benchO[0], benchmarkRegional: benchR[0] },
    { title: "EEG used (w/sedation, general anaesthesia)", meValue: me[1], benchmarkOverall: benchO[1], benchmarkRegional: benchR[1] },
    { title: "Capnography used (w/sedation)", meValue: me[2], benchmarkOverall: benchO[2], benchmarkRegional: benchR[2] },
    { title: "Noise Reduction Performed", meValue: me[3], benchmarkOverall: benchO[3], benchmarkRegional: benchR[3] },
    { title: "Cont. Temp. Monitoring", meValue: me[4], benchmarkOverall: benchO[4], benchmarkRegional: benchR[4] },
    { title: "Cont. Intraop. Monitoring used", meValue: me[5], benchmarkOverall: benchO[5], benchmarkRegional: benchR[5] },
  ];
}

// --- Week 0..week11 define the per-week values; "all" is computed as their mean below. ---

// --- Selection: Week 0 (most recent week: me better than benchmark for problem metrics, me < bench) ---
const week0PatientSatisfaction = buildPatientSatisfaction(
  [44, 49, 88], [37, 53, 83], [40, 50, 85]
);
const week0DataQuality = buildDataQuality(
  [46, 85, 69], [41, 88, 63], [43, 86, 66]
);
const w0_me = [7.2, 1.3, 18.2, 18.4, 1.8, 0.28, 11.6, 15.4, 4.0, 5.2, 5.0, 94.0];
const w0_count = [48, 65, 85, 120, 54, 28, 76, 90, 43, 65, 130, 152];
const w0_benchO = [21.2, 1.8, 19.5, 19.6, 2.2, 0.32, 12.8, 16.6, 4.9, 21.2, 6.2, 19.5];
const w0_benchR = [22.5, 2.0, 20.5, 18.6, 2.4, 0.33, 13.1, 17.2, 5.2, 22.5, 6.4, 21.4];
const week0QuattroMetrics = buildQuattroMetrics(
  ...Object.values(quattroFromPatientComfort(w0_me, w0_benchO, w0_benchR))
);
const week0PatientComfort = buildPatientComfort(w0_me, w0_count, w0_benchO, w0_benchR);
const week0Prevention = buildPrevention(
  [83, 70, 89, 66, 92, 86], [83, 70, 89, 66, 92, 86], [81, 72, 87, 68, 90, 84]
);

// --- Selection: Week 1 ---
const week1PatientSatisfaction = buildPatientSatisfaction(
  [40, 53, 92], [33, 57, 87], [36, 54, 89]
);
const week1DataQuality = buildDataQuality(
  [50, 89, 73], [45, 92, 67], [47, 90, 70]
);
const w1_me = [8.8, 2.6, 20.8, 21.0, 2.2, 0.55, 13.2, 17.2, 5.8, 6.8, 6.6, 96.2];
const w1_count = [42, 69, 93, 126, 58, 30, 80, 94, 47, 69, 138, 160];
const w1_benchO = [20.8, 3.0, 21.2, 21.3, 2.4, 0.51, 13.6, 17.4, 6.2, 20.8, 7.6, 21.2];
const w1_benchR = [22.2, 3.2, 22.2, 20.4, 2.6, 0.53, 14.0, 18.0, 6.4, 22.0, 7.8, 23.3];
const week1QuattroMetrics = buildQuattroMetrics(
  ...Object.values(quattroFromPatientComfort(w1_me, w1_benchO, w1_benchR))
);
const week1PatientComfort = buildPatientComfort(w1_me, w1_count, w1_benchO, w1_benchR);
const week1Prevention = buildPrevention(
  [87, 74, 93, 70, 96, 90], [87, 74, 93, 70, 96, 90], [85, 76, 91, 72, 94, 88]
);

// --- Selection: Week 2 ---
const week2PatientSatisfaction = buildPatientSatisfaction(
  [43, 50, 87], [36, 54, 84], [39, 51, 86]
);
const week2DataQuality = buildDataQuality(
  [47, 86, 70], [42, 89, 64], [44, 87, 67]
);
const w2_me = [10.2, 4.6, 22.8, 23.2, 2.8, 0.78, 14.4, 18.2, 7.6, 10.2, 9.6, 93.8];
const w2_count = [46, 66, 87, 121, 55, 32, 77, 91, 44, 66, 132, 154];
const w2_benchO = [20.6, 4.5, 20.3, 20.2, 2.7, 0.73, 14.8, 18.2, 8.0, 20.6, 9.6, 20.2];
const w2_benchR = [22.1, 4.7, 21.3, 19.4, 2.9, 0.75, 15.2, 18.8, 8.2, 21.9, 9.9, 22.3];
const week2QuattroMetrics = buildQuattroMetrics(
  ...Object.values(quattroFromPatientComfort(w2_me, w2_benchO, w2_benchR))
);
const week2PatientComfort = buildPatientComfort(w2_me, w2_count, w2_benchO, w2_benchR);
const week2Prevention = buildPrevention(
  [84, 71, 90, 67, 93, 87], [84, 71, 90, 67, 93, 87], [82, 73, 88, 69, 91, 85]
);

// --- Selection: Week 3 ---
const week3PatientSatisfaction = buildPatientSatisfaction(
  [41, 52, 89], [34, 56, 86], [37, 53, 88]
);
const week3DataQuality = buildDataQuality(
  [49, 88, 72], [44, 91, 66], [46, 89, 69]
);
const w3_me = [12.2, 6.2, 24.8, 25.4, 3.1, 1.08, 15.6, 18.8, 9.8, 12.2, 11.4, 95.6];
const w3_count = [44, 68, 91, 124, 57, 34, 79, 93, 46, 68, 136, 158];
const w3_benchO = [20.4, 5.8, 20.2, 20.3, 3.0, 1.01, 15.9, 19.0, 10.0, 20.4, 11.2, 20.2];
const w3_benchR = [22.0, 6.1, 22.3, 20.4, 3.2, 1.03, 16.3, 19.6, 10.2, 21.8, 11.6, 23.2];
const week3QuattroMetrics = buildQuattroMetrics(
  ...Object.values(quattroFromPatientComfort(w3_me, w3_benchO, w3_benchR))
);
const week3PatientComfort = buildPatientComfort(w3_me, w3_count, w3_benchO, w3_benchR);
const week3Prevention = buildPrevention(
  [86, 73, 92, 69, 95, 89], [86, 73, 92, 69, 95, 89], [84, 75, 90, 71, 93, 87]
);

// --- Selection: Week 4 ---
const week4PatientSatisfaction = buildPatientSatisfaction(
  [45, 48, 91], [38, 52, 82], [41, 49, 84]
);
const week4DataQuality = buildDataQuality(
  [45, 84, 68], [40, 87, 62], [42, 85, 65]
);
const w4_me = [14.2, 7.8, 26.8, 27.6, 3.4, 1.38, 16.8, 19.6, 12.2, 14.0, 13.2, 91.8];
const w4_count = [50, 64, 83, 118, 52, 30, 74, 88, 41, 64, 128, 150];
const w4_benchO = [20.3, 7.3, 18.4, 18.3, 3.4, 0.95, 17.1, 19.8, 12.0, 20.3, 12.9, 18.3];
const w4_benchR = [22.1, 7.5, 19.3, 17.4, 3.6, 0.98, 17.5, 20.4, 12.4, 21.7, 13.4, 20.2];
const week4QuattroMetrics = buildQuattroMetrics(
  ...Object.values(quattroFromPatientComfort(w4_me, w4_benchO, w4_benchR))
);
const week4PatientComfort = buildPatientComfort(w4_me, w4_count, w4_benchO, w4_benchR);
const week4Prevention = buildPrevention(
  [82, 69, 88, 65, 91, 85], [82, 69, 88, 65, 91, 85], [80, 71, 86, 67, 89, 83]
);

// --- Selection: Week 5 ---
const week5PatientSatisfaction = buildPatientSatisfaction(
  [39, 54, 93], [32, 58, 88], [35, 55, 90]
);
const week5DataQuality = buildDataQuality(
  [51, 90, 74], [46, 93, 68], [48, 91, 71]
);
const w5_me = [17.2, 9.8, 30.6, 31.8, 3.8, 1.73, 18.4, 20.6, 14.6, 16.2, 15.2, 97.4];
const w5_count = [40, 70, 95, 128, 60, 36, 82, 96, 49, 70, 140, 162];
const w5_benchO = [20.2, 9.1, 22.3, 22.2, 3.7, 1.28, 18.3, 20.6, 14.4, 20.2, 14.9, 22.3];
const w5_benchR = [22.0, 9.5, 23.3, 21.4, 3.9, 1.31, 18.7, 21.2, 14.9, 21.6, 15.4, 24.2];
const week5QuattroMetrics = buildQuattroMetrics(
  ...Object.values(quattroFromPatientComfort(w5_me, w5_benchO, w5_benchR))
);
const week5PatientComfort = buildPatientComfort(w5_me, w5_count, w5_benchO, w5_benchR);
const week5Prevention = buildPrevention(
  [88, 75, 94, 71, 97, 91], [88, 75, 94, 71, 97, 91], [86, 77, 92, 73, 95, 89]
);

// --- Selection: Week 6 ---
const week6PatientSatisfaction = buildPatientSatisfaction(
  [46, 47, 86], [39, 51, 81], [42, 48, 83]
);
const week6DataQuality = buildDataQuality(
  [44, 83, 67], [39, 86, 61], [41, 84, 64]
);
const w6_me = [19.4, 11.8, 28.2, 29.4, 4.0, 2.25, 19.2, 21.4, 17.2, 18.2, 17.6, 90.8];
const w6_count = [52, 63, 81, 116, 50, 32, 72, 86, 39, 62, 126, 148];
const w6_benchO = [20.1, 10.8, 17.3, 17.2, 4.0, 1.68, 19.5, 21.4, 16.6, 20.1, 17.4, 17.2];
const w6_benchR = [22.0, 11.3, 18.3, 16.4, 4.2, 1.73, 19.9, 22.1, 17.2, 21.5, 17.9, 19.3];
const week6QuattroMetrics = buildQuattroMetrics(
  ...Object.values(quattroFromPatientComfort(w6_me, w6_benchO, w6_benchR))
);
const week6PatientComfort = buildPatientComfort(w6_me, w6_count, w6_benchO, w6_benchR);
const week6Prevention = buildPrevention(
  [81, 68, 87, 64, 90, 84], [81, 68, 87, 64, 90, 84], [79, 70, 85, 66, 88, 82]
);

// --- Selection: Week 7 ---
const week7PatientSatisfaction = buildPatientSatisfaction(
  [38, 55, 94], [31, 59, 89], [34, 56, 91]
);
const week7DataQuality = buildDataQuality(
  [52, 91, 75], [47, 94, 69], [49, 92, 72]
);
const w7_me = [21.2, 13.6, 26.2, 27.4, 4.4, 2.90, 20.2, 22.4, 19.6, 20.2, 20.2, 98.4];
const w7_count = [38, 71, 97, 130, 62, 38, 84, 98, 51, 71, 142, 164];
const w7_benchO = [20.1, 12.8, 23.3, 23.2, 4.3, 2.18, 20.6, 22.3, 19.2, 20.1, 19.6, 23.3];
const w7_benchR = [21.9, 13.3, 24.3, 22.4, 4.5, 2.23, 21.1, 23.0, 19.9, 21.5, 20.2, 25.2];
const week7QuattroMetrics = buildQuattroMetrics(
  ...Object.values(quattroFromPatientComfort(w7_me, w7_benchO, w7_benchR))
);
const week7PatientComfort = buildPatientComfort(w7_me, w7_count, w7_benchO, w7_benchR);
const week7Prevention = buildPrevention(
  [89, 76, 95, 72, 98, 92], [89, 76, 95, 72, 98, 92], [87, 78, 93, 74, 96, 90]
);

// --- Selection: Week 8 ---
const week8PatientSatisfaction = buildPatientSatisfaction(
  [47, 46, 85], [40, 50, 80], [43, 47, 82]
);
const week8DataQuality = buildDataQuality(
  [43, 82, 66], [38, 85, 60], [40, 83, 63]
);
const w8_me = [23.2, 16.2, 24.2, 25.2, 4.8, 2.60, 21.4, 23.2, 22.2, 21.6, 22.6, 89.6];
const w8_count = [54, 62, 79, 114, 48, 30, 70, 84, 37, 60, 124, 146];
const w8_benchO = [20.1, 15.1, 16.3, 16.2, 4.6, 2.48, 21.8, 23.2, 21.6, 20.1, 22.2, 16.3];
const w8_benchR = [21.9, 15.5, 17.3, 15.4, 4.8, 2.53, 22.3, 23.9, 22.4, 21.4, 22.6, 18.3];
const week8QuattroMetrics = buildQuattroMetrics(
  ...Object.values(quattroFromPatientComfort(w8_me, w8_benchO, w8_benchR))
);
const week8PatientComfort = buildPatientComfort(w8_me, w8_count, w8_benchO, w8_benchR);
const week8Prevention = buildPrevention(
  [80, 67, 86, 63, 89, 83], [80, 67, 86, 63, 89, 83], [78, 69, 84, 65, 87, 81]
);

// --- Selection: Week 9 ---
const week9PatientSatisfaction = buildPatientSatisfaction(
  [37, 56, 95], [30, 60, 90], [33, 57, 92]
);
const week9DataQuality = buildDataQuality(
  [53, 92, 76], [48, 95, 70], [50, 93, 73]
);
const w9_me = [25.2, 18.8, 22.2, 23.2, 5.1, 5.35, 23.6, 24.2, 25.2, 22.6, 25.2, 99.4];
const w9_count = [36, 72, 99, 132, 64, 40, 86, 100, 53, 72, 144, 166];
const w9_benchO = [20.0, 17.5, 24.3, 24.2, 4.9, 2.93, 22.9, 24.1, 24.4, 20.0, 24.6, 24.2];
const w9_benchR = [21.8, 18.1, 25.3, 23.4, 5.2, 2.98, 23.4, 24.8, 25.2, 21.4, 24.9, 26.3];
const week9QuattroMetrics = buildQuattroMetrics(
  ...Object.values(quattroFromPatientComfort(w9_me, w9_benchO, w9_benchR))
);
const week9PatientComfort = buildPatientComfort(w9_me, w9_count, w9_benchO, w9_benchR);
const week9Prevention = buildPrevention(
  [90, 77, 96, 73, 99, 93], [90, 77, 96, 73, 99, 93], [88, 79, 94, 75, 97, 91]
);

// --- Selection: Week 10 ---
const week10PatientSatisfaction = buildPatientSatisfaction(
  [48, 45, 84], [41, 49, 79], [44, 46, 81]
);
const week10DataQuality = buildDataQuality(
  [42, 81, 65], [37, 84, 59], [39, 82, 62]
);
const w10_me = [26.8, 21.8, 20.2, 21.2, 5.4, 8.55, 24.8, 25.2, 28.2, 23.4, 28.2, 88.4];
const w10_count = [56, 61, 77, 112, 46, 34, 68, 82, 35, 58, 122, 144];
const w10_benchO = [20.0, 20.3, 15.3, 15.2, 5.2, 8.05, 24.1, 25.0, 27.4, 20.0, 27.6, 15.2];
const w10_benchR = [21.8, 20.9, 16.3, 14.4, 5.5, 8.25, 24.6, 25.7, 28.4, 21.3, 28.2, 17.3];
const week10QuattroMetrics = buildQuattroMetrics(
  ...Object.values(quattroFromPatientComfort(w10_me, w10_benchO, w10_benchR))
);
const week10PatientComfort = buildPatientComfort(w10_me, w10_count, w10_benchO, w10_benchR);
const week10Prevention = buildPrevention(
  [79, 66, 85, 62, 88, 82], [79, 66, 85, 62, 88, 82], [77, 68, 83, 64, 86, 80]
);

// --- Selection: Week 11 (oldest: me worse than benchmark for problem metrics, me > bench; Hypothermia me < bench) ---
const week11PatientSatisfaction = buildPatientSatisfaction(
  [36, 57, 96], [29, 61, 91], [32, 58, 93]
);
const week11DataQuality = buildDataQuality(
  [54, 93, 77], [49, 96, 71], [51, 94, 74]
);
const w11_me = [30.2, 26.2, 28.2, 29.2, 6.0, 11.92, 26.2, 27.0, 33.2, 26.2, 33.2, 97.0];
const w11_count = [34, 73, 101, 134, 66, 42, 88, 102, 55, 73, 146, 168];
const w11_benchO = [20.0, 22.8, 25.3, 25.2, 5.6, 11.25, 25.3, 25.9, 29.9, 20.0, 29.9, 98.2];
const w11_benchR = [21.8, 23.5, 26.3, 24.4, 5.9, 11.55, 25.8, 26.6, 30.6, 21.3, 30.4, 97.8];
const week11QuattroMetrics = buildQuattroMetrics(
  ...Object.values(quattroFromPatientComfort(w11_me, w11_benchO, w11_benchR))
);
const week11PatientComfort = buildPatientComfort(w11_me, w11_count, w11_benchO, w11_benchR);
const week11Prevention = buildPrevention(
  [91, 78, 97, 74, 100, 94], [91, 78, 97, 74, 100, 94], [89, 80, 95, 76, 98, 92]
);

// --- "All data" = mean of week0..week11 ---
const WEEK_SETS = [
  {
    patientSatisfaction: week0PatientSatisfaction,
    dataQuality: week0DataQuality,
    quattroMetrics: week0QuattroMetrics,
    patientComfort: week0PatientComfort,
    prevention: week0Prevention,
  },
  {
    patientSatisfaction: week1PatientSatisfaction,
    dataQuality: week1DataQuality,
    quattroMetrics: week1QuattroMetrics,
    patientComfort: week1PatientComfort,
    prevention: week1Prevention,
  },
  {
    patientSatisfaction: week2PatientSatisfaction,
    dataQuality: week2DataQuality,
    quattroMetrics: week2QuattroMetrics,
    patientComfort: week2PatientComfort,
    prevention: week2Prevention,
  },
  {
    patientSatisfaction: week3PatientSatisfaction,
    dataQuality: week3DataQuality,
    quattroMetrics: week3QuattroMetrics,
    patientComfort: week3PatientComfort,
    prevention: week3Prevention,
  },
  {
    patientSatisfaction: week4PatientSatisfaction,
    dataQuality: week4DataQuality,
    quattroMetrics: week4QuattroMetrics,
    patientComfort: week4PatientComfort,
    prevention: week4Prevention,
  },
  {
    patientSatisfaction: week5PatientSatisfaction,
    dataQuality: week5DataQuality,
    quattroMetrics: week5QuattroMetrics,
    patientComfort: week5PatientComfort,
    prevention: week5Prevention,
  },
  {
    patientSatisfaction: week6PatientSatisfaction,
    dataQuality: week6DataQuality,
    quattroMetrics: week6QuattroMetrics,
    patientComfort: week6PatientComfort,
    prevention: week6Prevention,
  },
  {
    patientSatisfaction: week7PatientSatisfaction,
    dataQuality: week7DataQuality,
    quattroMetrics: week7QuattroMetrics,
    patientComfort: week7PatientComfort,
    prevention: week7Prevention,
  },
  {
    patientSatisfaction: week8PatientSatisfaction,
    dataQuality: week8DataQuality,
    quattroMetrics: week8QuattroMetrics,
    patientComfort: week8PatientComfort,
    prevention: week8Prevention,
  },
  {
    patientSatisfaction: week9PatientSatisfaction,
    dataQuality: week9DataQuality,
    quattroMetrics: week9QuattroMetrics,
    patientComfort: week9PatientComfort,
    prevention: week9Prevention,
  },
  {
    patientSatisfaction: week10PatientSatisfaction,
    dataQuality: week10DataQuality,
    quattroMetrics: week10QuattroMetrics,
    patientComfort: week10PatientComfort,
    prevention: week10Prevention,
  },
  {
    patientSatisfaction: week11PatientSatisfaction,
    dataQuality: week11DataQuality,
    quattroMetrics: week11QuattroMetrics,
    patientComfort: week11PatientComfort,
    prevention: week11Prevention,
  },
];

function averageMetricArrays(weekArrays, keys) {
  return weekArrays[0].map((item, i) => {
    const out = { ...item };
    keys.forEach((key) => {
      const values = weekArrays.map((a) => a[i][key]);
      if (typeof values[0] === "number") {
        out[key] = round2(mean(values));
      }
    });
    return out;
  });
}

/** For patient comfort, Burst Suppression Time (index 5) is cumulative over weeks: sum instead of mean. */
function allPatientComfortWithCumulativeBurstSuppression(weekArrays, keys) {
  const averaged = averageMetricArrays(weekArrays, keys);
  const burstIndex = 5;
  keys.forEach((key) => {
    const values = weekArrays.map((a) => a[burstIndex][key]);
    if (typeof values[0] === "number") {
      averaged[burstIndex][key] = round2(values.reduce((s, v) => s + v, 0));
    }
  });
  return averaged;
}

const allPatientSatisfaction = averageMetricArrays(
  WEEK_SETS.map((s) => s.patientSatisfaction),
  ["meValue", "benchmarkOverall", "benchmarkRegional"]
);
const allDataQuality = averageMetricArrays(
  WEEK_SETS.map((s) => s.dataQuality),
  ["meValue", "benchmarkOverall", "benchmarkRegional"]
);
const allQuattroMetricsRaw = averageMetricArrays(
  WEEK_SETS.map((s) => s.quattroMetrics.slice(0, 4)),
  ["meValue", "benchmarkOverall", "benchmarkRegional"]
);
const allQuattroMetrics = [
  ...allQuattroMetricsRaw,
  { label: "QUATTRO", color: null },
];
const allPatientComfort = allPatientComfortWithCumulativeBurstSuppression(
  WEEK_SETS.map((s) => s.patientComfort),
  ["meValue", "count", "benchmarkOverall", "benchmarkRegional"]
);
const allPrevention = averageMetricArrays(
  WEEK_SETS.map((s) => s.prevention),
  ["meValue", "benchmarkOverall", "benchmarkRegional"]
);

/** Lookup key: "all" | "week0" | "week1" ... "week11" (week0 = first week in dropdown, i.e. most recent). */
export const DEFAULT_VALUES_BY_SELECTION = {
  all: {
    patientSatisfaction: allPatientSatisfaction,
    dataQuality: allDataQuality,
    quattroMetrics: allQuattroMetrics,
    patientComfort: allPatientComfort,
    prevention: allPrevention,
  },
  week0: {
    patientSatisfaction: week0PatientSatisfaction,
    dataQuality: week0DataQuality,
    quattroMetrics: week0QuattroMetrics,
    patientComfort: week0PatientComfort,
    prevention: week0Prevention,
  },
  week1: {
    patientSatisfaction: week1PatientSatisfaction,
    dataQuality: week1DataQuality,
    quattroMetrics: week1QuattroMetrics,
    patientComfort: week1PatientComfort,
    prevention: week1Prevention,
  },
  week2: {
    patientSatisfaction: week2PatientSatisfaction,
    dataQuality: week2DataQuality,
    quattroMetrics: week2QuattroMetrics,
    patientComfort: week2PatientComfort,
    prevention: week2Prevention,
  },
  week3: {
    patientSatisfaction: week3PatientSatisfaction,
    dataQuality: week3DataQuality,
    quattroMetrics: week3QuattroMetrics,
    patientComfort: week3PatientComfort,
    prevention: week3Prevention,
  },
  week4: {
    patientSatisfaction: week4PatientSatisfaction,
    dataQuality: week4DataQuality,
    quattroMetrics: week4QuattroMetrics,
    patientComfort: week4PatientComfort,
    prevention: week4Prevention,
  },
  week5: {
    patientSatisfaction: week5PatientSatisfaction,
    dataQuality: week5DataQuality,
    quattroMetrics: week5QuattroMetrics,
    patientComfort: week5PatientComfort,
    prevention: week5Prevention,
  },
  week6: {
    patientSatisfaction: week6PatientSatisfaction,
    dataQuality: week6DataQuality,
    quattroMetrics: week6QuattroMetrics,
    patientComfort: week6PatientComfort,
    prevention: week6Prevention,
  },
  week7: {
    patientSatisfaction: week7PatientSatisfaction,
    dataQuality: week7DataQuality,
    quattroMetrics: week7QuattroMetrics,
    patientComfort: week7PatientComfort,
    prevention: week7Prevention,
  },
  week8: {
    patientSatisfaction: week8PatientSatisfaction,
    dataQuality: week8DataQuality,
    quattroMetrics: week8QuattroMetrics,
    patientComfort: week8PatientComfort,
    prevention: week8Prevention,
  },
  week9: {
    patientSatisfaction: week9PatientSatisfaction,
    dataQuality: week9DataQuality,
    quattroMetrics: week9QuattroMetrics,
    patientComfort: week9PatientComfort,
    prevention: week9Prevention,
  },
  week10: {
    patientSatisfaction: week10PatientSatisfaction,
    dataQuality: week10DataQuality,
    quattroMetrics: week10QuattroMetrics,
    patientComfort: week10PatientComfort,
    prevention: week10Prevention,
  },
  week11: {
    patientSatisfaction: week11PatientSatisfaction,
    dataQuality: week11DataQuality,
    quattroMetrics: week11QuattroMetrics,
    patientComfort: week11PatientComfort,
    prevention: week11Prevention,
  },
};

// Weekly patient comfort for trend charts: index 0 = week0 (latest), index 11 = week11 (12 weeks ago).
export const WEEKLY_PATIENT_COMFORT_FOR_TRENDS = WEEK_SETS.map((s) => s.patientComfort);

// Legacy exports for DB path (Quattro colors); "all" set used as reference.
export const DEFAULT_PATIENT_SATISFACTION = allPatientSatisfaction;
export const DEFAULT_DATA_QUALITY = allDataQuality;
export const DEFAULT_QUATTRO_METRICS = allQuattroMetrics;
export const DEFAULT_PATIENT_COMFORT = allPatientComfort;
export const DEFAULT_PREVENTION = allPrevention;
